import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LoginForm from './LoginForm';

const AdminDashboard = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { t, language } = useLanguage();
  const [familyGroups, setFamilyGroups] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyUnconfirmed, setShowOnlyUnconfirmed] = useState(false);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showOnlyNotAttending, setShowOnlyNotAttending] = useState(false);
  const [retryingEmails, setRetryingEmails] = useState(new Set());

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('wedding_admin_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [familyGroupsRes, rsvpsRes] = await Promise.all([
        fetch('/api/family-groups', { headers }),
        fetch('/api/rsvps', { headers })
      ]);

      const familyGroupsData = await familyGroupsRes.json();
      const rsvpsData = await rsvpsRes.json();

      if (familyGroupsData.success) {
        setFamilyGroups(familyGroupsData.data);
      }

      if (rsvpsData.success) {
        // Deduplicate RSVPs: keep only the most recent one for each family group
        const rsvpsMap = new Map();
        rsvpsData.data.forEach(rsvp => {
          const familyGroupId = rsvp.familyGroupId?.toString();
          if (familyGroupId) {
            const existing = rsvpsMap.get(familyGroupId);
            if (!existing || new Date(rsvp.updatedAt || rsvp.createdAt) > new Date(existing.updatedAt || existing.createdAt)) {
              rsvpsMap.set(familyGroupId, rsvp);
            }
          }
        });
        setRsvps(Array.from(rsvpsMap.values()));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStats = () => {
    const totalInvited = familyGroups.reduce((sum, group) => sum + group.members.length, 0);
    
    const totalAttending = rsvps.reduce((sum, rsvp) => {
      return sum + (rsvp.attendingMembers || []).filter(member => member.attending === 'yes').length;
    }, 0);
    
    const totalNotAttending = rsvps.reduce((sum, rsvp) => {
      return sum + (rsvp.attendingMembers || []).filter(member => member.attending === 'no').length;
    }, 0);
    
    const totalNotReplied = totalInvited - totalAttending - totalNotAttending;
    
    const totalRsvpSent = familyGroups.reduce((sum, group) => sum + (group.rsvpSentCount || 0), 0);
    const familiesWithRsvpSent = familyGroups.filter(group => (group.rsvpSentCount || 0) > 0).length;

    return {
      totalAttending,
      totalNotAttending,
      totalNotReplied,
      totalRsvpSent,
      totalFamilyGroups: familyGroups.length,
      familiesWithRsvpSent
    };
  };

  const exportToCSV = () => {
    // Prepare CSV data with all guest information
    const csvData = [];
    
    // Add header row
    csvData.push([
      'First Name',
      'Last Name', 
      'Email',
      'Family Group',
      'Invitation Code',
      'Status',
      'Dietary Restrictions',
      'Submitted By',
      'Submission Date'
    ]);
    
    // Add data for each guest
    rsvps.forEach(rsvp => {
      const familyGroup = familyGroups.find(g => g._id === rsvp.familyGroupId);
      const submittedBy = rsvp.submittedBy ? `${rsvp.submittedBy.firstName} ${rsvp.submittedBy.lastName}` : 'Unknown';
      const submissionDate = new Date(rsvp.createdAt).toLocaleDateString();
      
      (rsvp.attendingMembers || []).forEach(member => {
        csvData.push([
          member.firstName,
          member.lastName,

          familyGroup?.groupName || 'Unknown',
          familyGroup?.invitationCode || 'Unknown',
          member.attending === 'yes' ? 'Attending' : 'Not Attending',
          member.dietaryRestrictions || 'None',
          submittedBy,
          submissionDate
        ]);
      });
    });
    
    // Convert to CSV string
    const csvString = csvData.map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
    
    // Create and download file
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `wedding-rsvps-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyInvitationLink = (invitationCode) => {
    const baseUrl = window.location.origin + (language === 'es' ? '/es' : '');
    const link = `${baseUrl}/rsvp?code=${invitationCode}`;
    navigator.clipboard.writeText(link);
  };

  const copyWebsiteLink = (invitationCode) => {
    const baseUrl = window.location.origin + (language === 'es' ? '/es' : '');
    const link = `${baseUrl}?code=${invitationCode}`;
    navigator.clipboard.writeText(link);
  };

  const updateRsvpSentCount = async (groupId, increment) => {
    try {
      const token = localStorage.getItem('wedding_admin_token');
      const response = await fetch(`/api/family-groups/${groupId}/rsvp-sent`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ increment })
      });

      if (response.ok) {
        const result = await response.json();
        // Update the local state
        setFamilyGroups(prevGroups => 
          prevGroups.map(group => 
            group._id === groupId 
              ? { ...group, rsvpSentCount: result.data.rsvpSentCount }
              : group
          )
        );
      } else {
        console.error('Failed to update RSVP sent count');
      }
    } catch (error) {
      console.error('Error updating RSVP sent count:', error);
    }
  };

  const retryEmail = async (rsvpId) => {
    try {
      setRetryingEmails(prev => new Set([...prev, rsvpId]));
      
      const token = localStorage.getItem('wedding_admin_token');
      const response = await fetch(`/api/rsvps/${rsvpId}/retry-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Email notification sent successfully!');
      } else {
        alert(`Failed to send email: ${result.message}`);
      }
    } catch (error) {
      console.error('Error retrying email:', error);
      alert('Error retrying email notification');
    } finally {
      setRetryingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(rsvpId);
        return newSet;
      });
    }
  };

  const stats = getStats();

  // Helper function to get the most recent RSVP for a family group
  const getLatestRSVP = (groupId) => {
    const groupIdStr = groupId?.toString();
    const matchingRSVPs = rsvps.filter(r => r.familyGroupId?.toString() === groupIdStr);
    if (matchingRSVPs.length === 0) return null;
    if (matchingRSVPs.length === 1) return matchingRSVPs[0];
    // If multiple, return the most recent one
    return matchingRSVPs.reduce((latest, current) => {
      const latestDate = new Date(latest.updatedAt || latest.createdAt);
      const currentDate = new Date(current.updatedAt || current.createdAt);
      return currentDate > latestDate ? current : latest;
    });
  };

  // Helper function to check if a family group has at least one unconfirmed member
  const hasUnconfirmedMember = (group) => {
    const rsvp = getLatestRSVP(group._id);
    
    // If no RSVP exists, all members are unconfirmed
    if (!rsvp) {
      return group.members.length > 0;
    }
    
    // Check if any member is not confirmed (not in attendingMembers or attending !== 'yes')
    return group.members.some(member => {
      const memberRsvp = (rsvp.attendingMembers || []).find(m => m.memberId === member._id);
      
      // Member not in RSVP or not attending
      if (!memberRsvp || memberRsvp.attending !== 'yes') {
        return true;
      }
      
      return false;
    });
  };

  // Helper function to check if a family group has at least one pending member
  const hasPendingMember = (group) => {
    const rsvp = getLatestRSVP(group._id);
    
    // If no RSVP exists, all members are pending
    if (!rsvp) {
      return group.members.length > 0;
    }
    
    // Check if any member is not in the RSVP (hasn't responded)
    return group.members.some(member => {
      const memberRsvp = (rsvp.attendingMembers || []).find(m => m.memberId === member._id);
      return !memberRsvp;
    });
  };

  // Helper function to check if a family group has at least one not attending member
  const hasNotAttendingMember = (group) => {
    const rsvp = getLatestRSVP(group._id);
    
    // If no RSVP exists, no one has responded "not attending"
    if (!rsvp) {
      return false;
    }
    
    // Check if any member responded with "no"
    return group.members.some(member => {
      const memberRsvp = (rsvp.attendingMembers || []).find(m => m.memberId === member._id);
      return memberRsvp && memberRsvp.attending === 'no';
    });
  };

  // Filter family groups based on search term and status filters
  const filteredFamilyGroups = familyGroups.filter(group => {
    // Apply status filters (if any are active)
    const hasActiveFilters = showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending;
    
    if (hasActiveFilters) {
      // Multiple filters work as OR - group must match at least one active filter
      let matchesFilter = false;
      
      if (showOnlyUnconfirmed && hasUnconfirmedMember(group)) {
        matchesFilter = true;
      }
      
      if (showOnlyPending && hasPendingMember(group)) {
        matchesFilter = true;
      }
      
      if (showOnlyNotAttending && hasNotAttendingMember(group)) {
        matchesFilter = true;
      }
      
      if (!matchesFilter) {
        return false;
      }
    }
    
    // Apply search term filter
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search by family group name
    if (group.groupName.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search by individual guest names
    return group.members.some(member => 
      member.firstName.toLowerCase().includes(searchLower) ||
      member.lastName.toLowerCase().includes(searchLower) ||
      `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower)
    );
  });

  // Function to highlight search terms in text
  const highlightSearchTerm = (text) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <motion.div 
          className="admin-dashboard"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header */}
          <div className="admin-header">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="admin-title">Admin Dashboard</h1>
                <p className="admin-subtitle">
                  Welcome, {user?.username}! Manage wedding RSVPs and guest information
                </p>
              </div>
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-success"
                  onClick={exportToCSV}
                >
                  <i className="fas fa-download me-2"></i>
                  Export CSV
                </button>
                <button 
                  className="btn btn-outline-danger"
                  onClick={logout}
                >
                  <i className="fas fa-sign-out-alt me-2"></i>
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Status Cards */}
          <div className="row mb-4">
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="status-card attending">
                <div className="status-icon">
                  <i className="fas fa-heart"></i>
                </div>
                <div className="status-content">
                  <h3>{stats.totalAttending}</h3>
                  <p>Attending</p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="status-card not-attending">
                <div className="status-icon">
                  <i className="fas fa-times-circle"></i>
                </div>
                <div className="status-content">
                  <h3>{stats.totalNotAttending}</h3>
                  <p>Not Attending</p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="status-card not-replied">
                <div className="status-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="status-content">
                  <h3>{stats.totalNotReplied}</h3>
                  <p>Not Replied</p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="status-card rsvp-sent">
                <div className="status-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="status-content">
                  <h3>{stats.totalRsvpSent}</h3>
                  <p>RSVP Sent (Total Count)</p>
                  <small className="text-muted">{stats.familiesWithRsvpSent} of {stats.totalFamilyGroups} families sent</small>
                  {stats.totalFamilyGroups > 0 && (
                    <div className="progress mt-2" style={{height: '4px'}}>
                      <div 
                        className="progress-bar bg-white" 
                        role="progressbar" 
                        style={{width: `${(stats.familiesWithRsvpSent / stats.totalFamilyGroups) * 100}%`}}
                        aria-valuenow={stats.familiesWithRsvpSent}
                        aria-valuemin="0"
                        aria-valuemax={stats.totalFamilyGroups}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="admin-tabs">
            <nav>
              <div className="nav nav-tabs">
                <button 
                  className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  Individual RSVPs
                </button>
                <button 
                  className={`nav-link ${activeTab === 'families' ? 'active' : ''}`}
                  onClick={() => setActiveTab('families')}
                >
                  Family Groups
                </button>
              </div>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'overview' && (
              <motion.div 
                className="tab-pane active"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Desktop Table View */}
                <div className="table-responsive d-none d-lg-block">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Family Group</th>
                        <th>Status</th>
                        <th>Message</th>
                        <th>Submitted By</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rsvps.flatMap(rsvp => 
                        (rsvp.attendingMembers || []).map((member, index) => (
                          <tr key={`${rsvp._id}-${index}`}>
                            <td>
                              <strong>{member.firstName} {member.lastName}</strong>
                            </td>
                            <td>
                              {familyGroups.find(g => g._id === rsvp.familyGroupId)?.groupName || 'Unknown'}
                            </td>
                            <td>
                              <span className={`badge ${
                                member.attending === 'yes' ? 'bg-success' : 'bg-danger'
                              }`}>
                                {member.attending === 'yes' ? 'Attending' : 'Not Attending'}
                              </span>
                            </td>
                            <td>
                              {rsvp.message ? (
                                <span 
                                  className="message-cell" 
                                  title={rsvp.message}
                                  style={{ 
                                    display: 'inline-block',
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {rsvp.message}
                                </span>
                              ) : '-'}
                            </td>
                            <td>
                              {rsvp.submittedBy ? `${rsvp.submittedBy.firstName} ${rsvp.submittedBy.lastName}` : '-'}
                            </td>
                            <td>
                              {new Date(rsvp.createdAt).toLocaleDateString()}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => retryEmail(rsvp._id)}
                                disabled={retryingEmails.has(rsvp._id)}
                                title="Retry email notification"
                              >
                                {retryingEmails.has(rsvp._id) ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Retrying...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-paper-plane me-1"></i>
                                    Retry Email
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="d-lg-none">
                  {rsvps.flatMap(rsvp => 
                    (rsvp.attendingMembers || []).map((member, index) => (
                      <div key={`${rsvp._id}-${index}`} className="mobile-rsvp-card mb-3">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="card-title mb-0">
                                <strong>{member.firstName} {member.lastName}</strong>
                              </h6>
                              <span className={`badge ${
                                member.attending === 'yes' ? 'bg-success' : 'bg-danger'
                              }`}>
                                {member.attending === 'yes' ? 'Attending' : 'Not Attending'}
                              </span>
                            </div>
                            
                            <div className="row g-2 mb-2">
                              <div className="col-6">
                                <small className="text-muted">Family Group</small>
                                <div className="fw-medium">
                                  {familyGroups.find(g => g._id === rsvp.familyGroupId)?.groupName || 'Unknown'}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Date</small>
                                <div className="fw-medium">
                                  {new Date(rsvp.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                            </div>

                            {rsvp.message && (
                              <div className="mb-2">
                                <small className="text-muted">Message</small>
                                <div className="fw-medium message-mobile" title={rsvp.message}>
                                  {rsvp.message}
                                </div>
                              </div>
                            )}

                            <div className="row g-2 mb-3">
                              <div className="col-6">
                                <small className="text-muted">Submitted By</small>
                                <div className="fw-medium">
                                  {rsvp.submittedBy ? `${rsvp.submittedBy.firstName} ${rsvp.submittedBy.lastName}` : '-'}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">RSVP ID</small>
                                <div className="fw-medium text-muted small">
                                  {rsvp._id.slice(-8)}
                                </div>
                              </div>
                            </div>

                            <div className="d-grid">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => retryEmail(rsvp._id)}
                                disabled={retryingEmails.has(rsvp._id)}
                                title="Retry email notification"
                              >
                                {retryingEmails.has(rsvp._id) ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Retrying...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-paper-plane me-1"></i>
                                    Retry Email
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === 'families' && (
              <motion.div 
                className="tab-pane active"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Search Bar and Filters */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="search-container">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by family name or guest name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setSearchTerm('')}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                      <div className="mt-3">
                        <div className="d-flex flex-wrap gap-3 align-items-center mb-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="filterUnconfirmed"
                              checked={showOnlyUnconfirmed}
                              onChange={(e) => setShowOnlyUnconfirmed(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="filterUnconfirmed">
                              <i className="fas fa-filter me-2"></i>
                              Unconfirmed
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="filterPending"
                              checked={showOnlyPending}
                              onChange={(e) => setShowOnlyPending(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="filterPending">
                              <i className="fas fa-clock me-2"></i>
                              Pending
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="filterNotAttending"
                              checked={showOnlyNotAttending}
                              onChange={(e) => setShowOnlyNotAttending(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="filterNotAttending">
                              <i className="fas fa-times-circle me-2"></i>
                              Not Attending
                            </label>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          {(searchTerm || showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending) && (
                            <div className="search-results-info">
                              <small className="text-muted">
                                Showing {filteredFamilyGroups.length} of {familyGroups.length} family groups
                              </small>
                            </div>
                          )}
                          {(showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending) && (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                setShowOnlyUnconfirmed(false);
                                setShowOnlyPending(false);
                                setShowOnlyNotAttending(false);
                              }}
                            >
                              <i className="fas fa-times me-1"></i>
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  {filteredFamilyGroups.length === 0 && (searchTerm || showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending) ? (
                    <div className="col-12">
                      <div className="text-center py-5">
                        <i className="fas fa-search fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">No family groups found</h5>
                        <p className="text-muted">
                          {searchTerm && (showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending)
                            ? 'Try adjusting your search or filter criteria'
                            : showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending
                            ? 'No family groups match the selected filter criteria'
                            : 'Try searching with different keywords'}
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                          {searchTerm && (
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => setSearchTerm('')}
                            >
                              Clear Search
                            </button>
                          )}
                          {(showOnlyUnconfirmed || showOnlyPending || showOnlyNotAttending) && (
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setShowOnlyUnconfirmed(false);
                                setShowOnlyPending(false);
                                setShowOnlyNotAttending(false);
                              }}
                            >
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    filteredFamilyGroups.map((group) => (
                    <div key={group._id} className="col-lg-6 col-md-12 mb-4">
                      <div className="family-card">
                        <div className="family-header">
                          <h5 dangerouslySetInnerHTML={{ __html: highlightSearchTerm(group.groupName) }}></h5>
                          <span className="invitation-code">Code: {group.invitationCode}</span>
                        </div>
                        <div className="family-members">
                          {group.members.map((member, index) => (
                            <div key={index} className="member-item">
                              <div className="member-info">
                                <strong dangerouslySetInnerHTML={{ __html: highlightSearchTerm(`${member.firstName} ${member.lastName}`) }}></strong>
                
                              </div>
                              <div className="member-status">
                                {(() => {
                                  const rsvp = getLatestRSVP(group._id);
                                  if (!rsvp) return <span className="badge bg-warning">Pending</span>;
                                  
                                  const memberRsvp = (rsvp.attendingMembers || []).find(m => m.memberId === member._id);
                                  if (!memberRsvp) return <span className="badge bg-warning">Pending</span>;
                                  
                                  return (
                                    <span className={`badge ${
                                      memberRsvp.attending === 'yes' ? 'bg-success' : 'bg-danger'
                                    }`}>
                                      {memberRsvp.attending === 'yes' ? 'Attending' : 'Not Attending'}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="family-actions">
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => copyInvitationLink(group.invitationCode)}
                            >
                              <i className="fas fa-copy me-1"></i>
                              <span className="d-none d-sm-inline">Copy Invitation Link</span>
                              <span className="d-sm-none">Invitation</span>
                            </button>
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => copyWebsiteLink(group.invitationCode)}
                            >
                              <i className="fas fa-copy me-1"></i>
                              <span className="d-none d-sm-inline">Copy website Link</span>
                              <span className="d-sm-none">Website</span>
                            </button>
                            <div className="rsvp-sent-count-control">
                              <div className="d-flex align-items-center gap-2">
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => updateRsvpSentCount(group._id, -1)}
                                  disabled={(group.rsvpSentCount || 0) <= 0}
                                  title="Decrease count"
                                >
                                  <i className="fas fa-minus"></i>
                                </button>
                                <span className="rsvp-count-display">
                                  <span className="fw-bold">{group.rsvpSentCount || 0}</span>
                                  <small className="text-muted d-block">RSVP Sent</small>
                                </span>
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => updateRsvpSentCount(group._id, 1)}
                                  title="Increase count"
                                >
                                  <i className="fas fa-plus"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
